/**
 * Inventario por sucursal. Lo usan la consola admin (stock, kardex, operaciones,
 * conteo) y el POS (aviso de stock en el carrito).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api.ts";
import type {
  CountResultOut,
  InventoryMovementOut,
  KardexOut,
  ManualMovementType,
  StockOut,
  TransferOut,
} from "@/types/api.ts";

export const inventoryKeys = {
  all: ["inventory"] as const,
  stock: (branchId: string, q: string, lowOnly: boolean) =>
    [...inventoryKeys.all, "stock", branchId, q, lowOnly] as const,
  kardex: (branchId: string, productId: string) =>
    [...inventoryKeys.all, "kardex", branchId, productId] as const,
  transfers: (branchId: string) => [...inventoryKeys.all, "transfers", branchId] as const,
};

interface StockVars {
  branchId: string | null | undefined;
  q?: string;
  lowOnly?: boolean;
}

export function useStock({ branchId, q = "", lowOnly = false }: StockVars) {
  return useQuery({
    queryKey: inventoryKeys.stock(branchId ?? "", q, lowOnly),
    queryFn: () =>
      api.get<StockOut[]>("/inventory/stock", {
        searchParams: { branch_id: branchId, q: q || undefined, low_only: lowOnly, limit: 1000 },
      }),
    enabled: Boolean(branchId),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useKardex(branchId: string | null | undefined, productId: string | null) {
  return useQuery({
    queryKey: inventoryKeys.kardex(branchId ?? "", productId ?? ""),
    queryFn: () =>
      api.get<KardexOut>(`/inventory/products/${productId}/kardex`, {
        searchParams: { branch_id: branchId, limit: 200 },
      }),
    enabled: Boolean(branchId && productId),
  });
}

export interface MovementBatchInput {
  branch_id: string;
  movement_type: ManualMovementType;
  document?: string | null;
  notes?: string | null;
  lines: { product_id: string; quantity: string; unit_cost?: string | null }[];
}

export function useRecordMovements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MovementBatchInput) =>
      api.post<InventoryMovementOut[]>("/inventory/movements", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

export interface CountInput {
  branch_id: string;
  notes?: string | null;
  lines: { product_id: string; counted: string }[];
}

export function useApplyCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CountInput) => api.post<CountResultOut[]>("/inventory/counts", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

// ---------- Traslados entre sucursales ------------------------------------------

export function useTransfers(branchId: string | null | undefined) {
  return useQuery({
    queryKey: inventoryKeys.transfers(branchId ?? ""),
    queryFn: () =>
      api.get<TransferOut[]>("/inventory/transfers", {
        searchParams: { branch_id: branchId, limit: 200 },
      }),
    enabled: Boolean(branchId),
    refetchInterval: 30_000,
  });
}

export interface TransferInput {
  from_branch_id: string;
  to_branch_id: string;
  notes?: string | null;
  lines: { product_id: string; quantity: string }[];
}

export function useSendTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransferInput) => api.post<TransferOut>("/inventory/transfers", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

export function useReceiveTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      lines: { product_id: string; received_quantity: string }[];
    }) => api.post<TransferOut>(`/inventory/transfers/${vars.id}/receive`, { lines: vars.lines }),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

export function useCancelTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<TransferOut>(`/inventory/transfers/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}
